/**
 * Separate Typography Specifications.
 *
 * Defines explicit font-family, font-weight, and letter-spacing for each text role.
 * Applied across themes when a theme font preset is selected, strictly preserving
 * existing base font variables.
 *
 * Common and extensible architecture ready for future theme fonts.
 */

export const PURE_TYPOGRAPHY = {
  // 1. Main Section Titles — Outfit 300 (font-light), tracking 3.5px
  sectionHeading: 'font-outfit font-light tracking-[3.5px]',

  // 2. Creator Profile Section
  profileName: 'font-noto font-normal tracking-normal',
  profileUserName: 'font-noto font-normal tracking-normal',
  profilePairValue: 'font-noto font-normal tracking-normal',
  profilePairCaption: 'font-outfit font-light tracking-[0.15rem]',
  socialChannelsStat: 'font-notodisplay font-medium tracking-normal',
  socialChannelsCaption: 'font-outfit font-light tracking-normal',
  profileBioHeading: 'font-outfit font-light tracking-[3.5px]',
  profileBioText: 'font-outfit',

  // 3. Deliverables / Offerings
  deliverablesHeading: 'font-outfit font-light tracking-[3.5px]',
  deliverableTitle: 'font-notodisplay font-medium tracking-normal',
  deliverableDescription: 'font-noto font-normal tracking-normal',

  // 4. Rates
  ratesHeading: 'font-outfit font-light tracking-[3.5px]',
  rateTitle: 'font-notodisplay font-light md:font-medium tracking-[1px]',
  ratePrice: 'font-noto font-light tracking-normal',

  // 5. Links
  linksHeading: 'font-outfit font-light tracking-[3.5px]',
  linkTitle: 'font-notodisplay font-medium tracking-normal',

  // 6. Top Content / Posts
  topContentHeading: 'font-outfit font-light tracking-[3.5px]',
  topContentStatValue: 'font-notodisplay tracking-normal',
  topContentStatLabel: 'font-outfit tracking-normal', // Updated to use Outfit font as per figma design guidelines
  topContentTitle: 'font-notodisplay font-medium tracking-normal',
  topContentSubtitle: 'font-outfit font-light tracking-normal',

  // 7. Audience
  audienceHeading: 'font-outfit font-light tracking-[3.5px]',
  audienceSelectedHeading: 'font-outfit tracking-[3.5px]',
  audienceSubHeading: 'font-outfit tracking-widest',
  audienceCountryName: 'font-noto',
  audiencePercentage: 'font-noto font-semibold tracking-normal',
  audienceCityName: 'font-noto font-medium tracking-normal',
  audienceCityPercentage: 'font-noto font-semibold tracking-normal',
  audienceAgeBracket: 'font-noto font-light tracking-normal',
  audienceAgePercentage: 'font-noto font-semibold tracking-normal',
  audienceGenderValue: 'font-noto tracking-normal',

  // 8. Favourite Facts
  favFactValue: 'font-notodisplay font-normal tracking-normal',
  favFactCaption: 'font-outfit font-light tracking-[3px]',

  // 9. Brand Partners
  brandPartnersHeading: 'font-outfit font-light tracking-[3.5px]',
  brandPartnerName: 'font-notodisplay tracking-[1px]',

  // 10. Brand Reviews
  brandReviewsHeading: 'font-outfit font-light tracking-[3.5px]',
  reviewCreatorName: 'font-notodisplay font-light tracking-normal',
  reviewSubLine: 'font-baskervville tracking-normal',
  reviewOverallRating: 'font-notodisplay font-medium tracking-normal',
  reviewOverallCaption: 'font-outfit font-light tracking-[3px]',
  reviewCardRatingLabel: 'font-outfit font-light tracking-[15%]', //letter-spacing: 15%; as per figma
  reviewCardName: 'font-notodisplay max-md:font-medium tracking-widest',
  reviewCardBrandName: 'font-noto font-light tracking-normal md:tracking-widest',
  reviewCardDate: 'font-outfit tracking-normal  max-md:tracking-[3px]',
  reviewCardText: 'font-outfit max-md:font-light tracking-normal',
  reviewCardVerifiedBadge: 'font-outfit font-light tracking-[3px]',

  // 11. Top Interests
  topInterestHeading: 'font-outfit font-light tracking-[3.5px]',
  topInterestItem: 'font-notodisplay font-normal tracking-normal',

  // 12. CTA Banner
  ctaBannerTitle: 'font-notodisplay font-medium tracking-normal',
  ctaBannerButton: 'font-outfit tracking-widest',
  ctaBannerLabel: 'font-outfit font-medium tracking-normal',

  // 13. Socials & Email
  socialsHeading: 'font-outfit font-light tracking-[3.5px]',
  socialHandle: 'font-outfit font-light tracking-normal',
  mailHeading: 'font-outfit font-light tracking-[3.5px]',
  mailAddress: 'font-outfit font-light tracking-normal',

  // 14. Verified Badge & Statement
  verifiedBadge: 'font-outfit font-normal tracking-[3px]',
  verificationStatement: 'font-noto font-light tracking-normal',

  // 15. Editorial Video Modal Title
  editorialVideoTitle: 'font-baskervville font-normal italic tracking-normal',
};

export const DARK_TYPOGRAPHY = {
  // 1. Main Section Titles — Roboto 300 (font-light), tracking 3px
  sectionHeading: 'font-roboto font-light max-md:tracking-[3px]',

  // 2. Creator Profile Section
  profileName: 'font-antonio font-bold tracking-normal',
  profileUserName: 'font-antonio font-normal tracking-normal',
  profilePairValue: 'font-roboto font-normal tracking-normal',
  profilePairCaption: 'font-roboto font-light tracking-[3px]',
  socialChannelsStat: 'font-antonio font-medium tracking-normal',
  socialChannelsCaption: 'font-outfit font-light tracking-normal',
  profileBioHeading: 'font-roboto font-light max-md:tracking-[3px]',
  profileBioText: 'font-roboto font-normal tracking-normal',

  // 3. Deliverables / Offerings
  deliverablesHeading: 'font-roboto font-light max-md:tracking-[3px]',
  deliverableTitle: 'font-antonio font-medium tracking-normal',
  deliverableDescription: 'font-roboto font-normal tracking-normal',

  // 4. Rates
  ratesHeading: 'font-roboto font-light max-md:tracking-[3px]',
  rateTitle: 'font-antonio font-light tracking-normal',
  ratePrice: 'font-antonio font-thin tracking-normal',

  // 5. Links
  linksHeading: 'font-roboto font-light max-md:tracking-[3px]',
  linkTitle: 'font-antonio font-semibold tracking-normal',

  // 6. Top Content / Posts
  topContentHeading: 'font-roboto font-light max-md:tracking-[3px]',
  topContentStatValue: 'font-antonio tracking-normal',
  topContentStatLabel: 'font-roboto tracking-normal',
  topContentTitle: 'font-antonio font-semibold tracking-normal',
  topContentSubtitle: 'font-roboto font-normal tracking-normal',

  // 7. Audience
  audienceHeading: 'font-roboto font-light tracking-[3px]',
  audienceSelectedHeading: 'font-roboto font-normal tracking-[3px]',
  audienceSubHeading: 'font-outfit font-light tracking-[3px]',
  audienceCountryName: 'font-outfit font-light tracking-normal',
  audiencePercentage: 'font-noto font-semibold tracking-normal',
  audienceCityName: 'font-antonio font-semibold tracking-normal',
  audienceCityPercentage: 'font-noto font-semibold tracking-normal',
  audienceAgeBracket: 'font-outfit font-light tracking-normal',
  audienceAgePercentage: 'font-outfit font-medium tracking-normal',
  audienceGenderValue: 'font-roboto font-normal tracking-normal',

  // 8. Favourite Facts
  favFactValue: 'font-antonio font-semibold tracking-normal',
  favFactCaption: 'font-roboto font-light tracking-[3px]',

  // 9. Brand Partners
  brandPartnersHeading: 'font-roboto font-light max-md:tracking-[3px]',
  brandPartnerName: 'font-notodisplay tracking-[1px]',

  // 10. Brand Reviews
  brandReviewsHeading: 'font-roboto font-light max-md:tracking-[3px]',
  reviewCreatorName: 'font-antonio font-normal tracking-normal',
  reviewSubLine: 'font-roboto font-light tracking-normal',
  reviewOverallRating: 'font-antonio font-semibold tracking-normal',
  reviewOverallCaption: 'font-roboto font-light tracking-[3px]',
  reviewCardRatingLabel: 'font-roboto font-light tracking-[3px]',
  reviewCardName: 'font-notodisplay md:font-antonio font-bold md:font-normal tracking-[3px]',
  reviewCardBrandName: 'font-outfit font-light tracking-normal',
  reviewCardDate: 'font-outfit tracking-normal max-md:tracking-[3px]',
  reviewCardText: 'font-outfit md:font-light tracking-normal',
  reviewCardVerifiedBadge: 'font-nunito font-light tracking-[3px]',

  // 11. Top Interests
  topInterestHeading: 'font-roboto font-light max-md:tracking-[3px]',
  topInterestItem: 'font-antonio font-semibold tracking-normal',

  // 12. CTA Banner
  ctaBannerTitle: 'font-antonio font-semibold tracking-normal',
  ctaBannerButton: 'font-roboto font-bold tracking-normal',
  ctaBannerLabel: 'font-antonio font-bold tracking-normal',

  // 13. Socials & Email
  socialsHeading: 'font-light max-md:tracking-[3px]',
  socialHandle: 'font-outfit font-light tracking-normal',
  mailHeading: 'font-roboto font-light max-md:tracking-[3px]',
  mailAddress: 'font-outfit font-light tracking-normal',

  // 14. Verified Badge & Statement
  verifiedBadge: 'font-outfit font-normal tracking-normal',
  verificationStatement: 'font-noto font-light tracking-normal',

  // 15. Editorial Video Modal Title
  editorialVideoTitle: 'font-baskervville font-normal tracking-normal',
};

export const NATURAL_TYPOGRAPHY = {
  // 1. Main Section Titles — Nunito Sans 500 (font-medium), tracking 3px
  sectionHeading: 'font-nunitosans font-medium tracking-[3px]',

  // 2. Creator Profile Section
  profileName: 'font-mollie font-normal tracking-normal',
  profileUserName: 'font-mollie font-normal tracking-normal',
  profilePairValue: 'font-nunito font-medium tracking-normal',
  profilePairCaption: 'font-nunito font-light tracking-[3px]',
  socialChannelsStat: 'font-nunitosans font-medium tracking-normal',
  socialChannelsCaption: 'font-nunitosans font-light tracking-normal',
  profileBioHeading: 'font-nunitosans font-medium tracking-[3px]',
  profileBioText: 'font-nunito tracking-normal',

  // 3. Deliverables / Offerings
  deliverablesHeading: 'font-nunitosans font-medium tracking-[3px]',
  deliverableTitle: 'font-nunito font-medium tracking-normal',
  deliverableDescription: 'font-nunito font-normal tracking-normal',

  // 4. Rates
  ratesHeading: 'font-nunitosans font-medium tracking-[3px]',
  rateTitle: 'font-nunito font-medium tracking-normal',
  ratePrice: 'font-nunito font-light tracking-normal',

  // 5. Links
  linksHeading: 'font-nunitosans font-medium tracking-[3px]',
  linkTitle: 'font-nunito font-medium tracking-normal',

  // 6. Top Content / Posts
  topContentHeading: 'font-nunitosans font-medium tracking-[3px]',
  topContentStatValue: 'font-nunito font-medium tracking-normal',
  topContentStatLabel: 'font-nunito font-light tracking-normal',
  topContentTitle: 'font-nunito font-semibold tracking-normal',
  topContentSubtitle: 'font-nunito font-light tracking-normal',

  // 7. Audience
  audienceHeading: 'font-nunitosans font-light tracking-[3px]',
  audienceSelectedHeading: 'font-nunitosans font-normal tracking-[3px]',
  audienceSubHeading: 'font-nunito font-semibold tracking-[0.125rem]',
  audienceCountryName: 'font-nunito font-light tracking-normal',
  audiencePercentage: 'font-nunito font-semibold tracking-normal',
  audienceCityName: 'font-nunito font-medium tracking-normal',
  audienceCityPercentage: 'font-nunito font-semibold tracking-normal',
  audienceAgeBracket: 'font-nunito font-bold tracking-normal',
  audienceAgePercentage: 'font-nunito font-bold tracking-normal',
  audienceGenderValue: 'font-nunito tracking-normal',

  // 8. Favourite Facts
  favFactValue: 'font-nunito font-semibold tracking-normal',
  favFactCaption: 'font-nunitosans font-semibold tracking-[3px]',

  // 9. Brand Partners
  brandPartnersHeading: 'font-nunitosans font-medium tracking-[3px]',
  brandPartnerName: 'font-notodisplay tracking-[1px]',

  // 10. Brand Reviews
  brandReviewsHeading: 'font-nunitosans font-medium tracking-[3px]',
  reviewCreatorName: 'font-mollie font-normal tracking-normal',
  reviewSubLine: 'font-baskervville font-normal tracking-normal',
  reviewOverallRating: 'font-nunito font-medium tracking-normal',
  reviewOverallCaption: 'font-nunito font-light tracking-[0.125rem]',
  reviewCardRatingLabel: 'font-nunito font-light tracking-wider',
  reviewCardName: 'font-nunitosans font-bold md:font-normal tracking-[3px] md:tracking-normal',
  reviewCardBrandName: 'font-outfit font-light tracking-normal',
  reviewCardDate: 'font-outfit font-light tracking-normal',
  reviewCardText: 'font-nunito tracking-normal md:font-light',
  reviewCardVerifiedBadge: 'font-nunito font-light tracking-[0.125rem]',

  // 11. Top Interests
  topInterestHeading: 'font-nunitosans font-medium tracking-[3px]',
  topInterestItem: 'font-nunito font-medium tracking-normal',

  // 12. CTA Banner
  ctaBannerTitle: 'font-nunito font-bold tracking-normal',
  ctaBannerButton: 'font-nunito font-bold tracking-[0.25rem]',
  ctaBannerLabel: 'font-nunito font-light tracking-normal',

  // 13. Socials & Email
  socialsHeading: 'font-nunitosans font-medium tracking-[3px]',
  socialHandle: 'font-outfit font-light tracking-normal',
  mailHeading: 'font-nunitosans font-medium tracking-[3px]',
  mailAddress: 'font-outfit font-light tracking-normal',

  // 14. Verified Badge & Statement
  verifiedBadge: 'font-nunito font-bold tracking-[3px]',
  verificationStatement: 'font-roboto font-light tracking-normal',

  // 15. Editorial Video Modal Title
  editorialVideoTitle: 'font-baskervville tracking-normal',
};

export const CLASSIC_TYPOGRAPHY = {
  // 1. Main Section Titles — Roboto 500 (font-medium), tracking 3px
  sectionHeading: 'font-roboto font-medium tracking-[3px]',

  // 2. Creator Profile Section
  profileName: 'font-roboto font-bold tracking-normal',
  profileUserName: 'font-roboto font-light tracking-normal',
  profilePairValue: 'font-roboto font-medium tracking-normal',
  profilePairCaption: 'font-roboto font-light tracking-[3px]',
  socialChannelsStat: 'font-notodisplay font-medium tracking-normal',
  socialChannelsCaption: 'font-outfit font-light tracking-normal',
  profileBioHeading: 'font-roboto font-medium tracking-[3px]',
  profileBioText: 'font-roboto tracking-normal',

  // 3. Deliverables / Offerings
  deliverablesHeading: 'font-roboto font-medium tracking-[3px]',
  deliverableTitle: 'font-roboto font-medium tracking-normal',
  deliverableDescription: 'font-roboto font-normal tracking-normal',

  // 4. Rates
  ratesHeading: 'font-roboto font-medium tracking-[3px]',
  rateTitle: 'font-roboto font-light md:font-medium tracking-normal',
  ratePrice: 'font-roboto font-light tracking-normal',

  // 5. Links
  linksHeading: 'font-roboto font-medium tracking-[3px]',
  linkTitle: 'font-roboto  font-light md:font-medium tracking-normal',

  // 6. Top Content / Posts
  topContentHeading: 'font-roboto font-medium tracking-[3px]',
  topContentStatValue: 'font-roboto font-medium tracking-normal',
  topContentStatLabel: 'font-roboto font-light tracking-normal',
  topContentTitle: 'font-roboto font-semibold tracking-normal',
  topContentSubtitle: 'font-roboto font-normal tracking-normal',

  // 7. Audience
  audienceHeading: 'font-roboto font-light tracking-normal',
  audienceSelectedHeading: 'font-roboto tracking-normal',
  audienceSubHeading: 'font-outfit font-light tracking-widest',
  audienceCountryName: 'font-outfit font-light tracking-normal',
  audiencePercentage: 'font-roboto font-medium tracking-normal',
  audienceCityName: 'font-roboto font-semibold tracking-normal',
  audienceCityPercentage: 'font-noto font-semibold tracking-normal',
  audienceAgeBracket: 'font-outfit font-light tracking-normal',
  audienceAgePercentage: 'font-outfit font-medium tracking-normal',
  audienceGenderValue: 'font-roboto font-normal tracking-normal',

  // 8. Favourite Facts
  favFactValue: 'font-roboto font-medium tracking-normal',
  favFactCaption: 'font-outfit font-light tracking-[3px]',

  // 9. Brand Partners
  brandPartnersHeading: 'font-roboto font-medium tracking-[3px]',
  brandPartnerName: 'font-notodisplay tracking-[1px]',

  // 10. Brand Reviews
  brandReviewsHeading: 'font-roboto font-medium tracking-[3px]',
  reviewCreatorName: 'font-roboto font-normal tracking-normal',
  reviewSubLine: 'font-roboto max-md:font-baskervville font-light tracking-normal',
  reviewOverallRating: 'font-notodisplay font-medium tracking-normal',
  reviewOverallCaption: 'font-outfit font-light tracking-[3px]',
  reviewCardRatingLabel: 'font-roboto font-light tracking-[3px]',
  reviewCardName: 'font-notodisplay font-bold md:font-normal tracking-normal md:tracking-[3px]',
  reviewCardBrandName: 'font-outfit font-light tracking-normal',
  reviewCardDate: 'font-outfit font-light tracking-normal md:tracking-[3px]',
  reviewCardText: 'font-outfit font-light tracking-normal',
  reviewCardVerifiedBadge: 'font-outfit font-light tracking-[3px]',

  // 11. Top Interests
  topInterestHeading: 'font-roboto font-medium tracking-[3px]',
  topInterestItem: 'font-roboto font-light tracking-[3px]',

  // 12. CTA Banner
  ctaBannerTitle: 'font-roboto font-medium tracking-normal',
  ctaBannerButton: 'max-md:font-roboto md:font-outfit tracking-widest',
  ctaBannerLabel: 'font-roboto font-medium tracking-normal',

  // 13. Socials & Email
  socialsHeading: 'font-roboto font-medium tracking-[3px]',
  socialHandle: 'font-outfit font-light tracking-normal',
  mailHeading: 'font-roboto font-medium tracking-[3px]',
  mailAddress: 'font-outfit font-light tracking-normal',

  // 14. Verified Badge & Statement
  verifiedBadge: 'font-outfit font-light tracking-[3px]',
  verificationStatement: 'font-roboto font-light tracking-normal',

  // 15. Editorial
  editorialVideoTitle: 'font-baskervville tracking-normal',
};

export const COMBO_TYPOGRAPHY = {
  // 1. Main Section Titles — Syne 600 (font-semibold), tracking 3px
  sectionHeading: 'font-syne font-semibold tracking-[3px]',

  // 2. Creator Profile Section
  profileName: 'font-syne font-bold tracking-normal',
  profileUserName: 'font-worksans font-medium tracking-normal',
  profilePairValue: 'font-worksans font-normal tracking-normal',
  profilePairCaption: 'font-spacemono font-normal tracking-[3px]',
  profileStatCaption: 'font-spacemono font-normal tracking-normal',
  socialChannelsStat: 'font-syne font-bold tracking-normal',
  socialChannelsCaption: 'font-spacemono font-normal tracking-normal',
  profileBioHeading: 'font-syne font-semibold tracking-[3px]',
  profileBioText: 'font-fraunces font-normal italic tracking-normal',

  // 3. Deliverables / Offerings
  deliverablesHeading: 'font-syne font-semibold tracking-[3px]',
  deliverableTitle: 'font-worksans font-medium tracking-normal',
  deliverableDescription: 'font-worksans font-normal tracking-normal',

  // 4. Rates
  ratesHeading: 'font-syne font-semibold tracking-[3px]',
  rateTitle: 'font-worksans font-normal md:font-medium tracking-normal',
  ratePrice: 'font-worksans font-normal md:font-medium tracking-normal',

  // 5. Links
  linksHeading: 'font-syne font-semibold tracking-[3px]',
  linkTitle: 'font-worksans font-medium tracking-normal',

  // 6. Top Content / Posts
  topContentHeading: 'font-syne font-semibold tracking-[3px]',
  topContentStatValue: 'font-syne font-bold tracking-normal',
  topContentStatLabel: 'font-spacemono font-normal tracking-normal',
  topContentTitle: 'font-worksans font-medium tracking-normal',
  topContentSubtitle: 'font-fraunces font-normal italic tracking-normal',

  // 7. Audience
  audienceHeading: 'font-syne font-semibold tracking-normal',
  audienceSelectedHeading: 'font-syne font-semibold tracking-normal',
  audienceSubHeading: 'font-spacemono font-normal tracking-widest',
  audienceCountryName: 'font-worksans font-normal tracking-normal',
  audiencePercentage: 'font-syne font-bold tracking-normal',
  audienceCityName: 'font-worksans font-medium tracking-normal',
  audienceCityPercentage: 'font-syne font-bold tracking-normal',
  audienceAgeBracket: 'font-worksans font-normal tracking-normal',
  audienceAgePercentage: 'font-worksans font-medium tracking-normal',
  audienceGenderValue: 'font-worksans font-normal md:font-medium tracking-normal',

  // 8. Favourite Facts
  favFactValue: 'font-syne font-bold tracking-normal',
  favFactCaption: 'font-spacemono font-normal tracking-[3px]',

  // 9. Brand Partners
  brandPartnersHeading: 'font-syne font-semibold tracking-[3px]',
  brandPartnerName: 'font-worksans font-medium tracking-[1px]',

  // 10. Brand Reviews
  brandReviewsHeading: 'font-syne font-semibold tracking-[3px]',
  reviewCreatorName: 'font-worksans font-medium tracking-normal',
  reviewSubLine: 'font-worksans font-normal tracking-normal',
  reviewOverallRating: 'font-syne font-bold tracking-normal',
  reviewOverallCaption: 'font-spacemono font-normal tracking-[3px]',
  reviewCardRatingLabel: 'font-spacemono font-normal tracking-wider',
  reviewCardName: 'font-worksans font-medium tracking-normal md:tracking-[3px]',
  reviewCardBrandName: 'font-worksans font-medium tracking-normal',
  reviewCardDate: 'font-spacemono font-normal tracking-normal md:tracking-[3px]',
  reviewCardDescription: 'font-worksans font-normal tracking-normal',
  reviewCardText: 'font-worksans font-normal tracking-normal',
  reviewCardVerifiedBadge: 'font-spacemono font-normal tracking-[3px]',

  // 11. Top Interests
  topInterestHeading: 'font-syne font-semibold tracking-[3px]',
  topInterestItem: 'font-worksans font-normal md:font-medium tracking-[3px]',

  // 12. CTA Banner
  ctaBannerTitle: 'font-worksans font-medium tracking-normal',
  ctaBannerButton: 'font-worksans font-semibold tracking-[0.25rem]',
  ctaBannerLabel: 'font-worksans font-semibold tracking-normal',

  // 13. Socials & Email
  socialsHeading: 'font-syne font-semibold tracking-[3px]',
  socialHandle: 'font-worksans font-medium tracking-normal',
  mailHeading: 'font-syne font-semibold tracking-[3px]',
  mailAddress: 'font-worksans font-medium tracking-normal',

  // 14. Verified Badge & Statement
  verifiedBadge: 'font-spacemono font-normal tracking-[3px]',
  verificationStatement: 'font-worksans font-normal tracking-normal',

  // 15. Editorial
  editorialVideoTitle: 'font-worksans font-medium tracking-normal',
};

export const TYPOGRAPHY_THEMES = {
  standard: PURE_TYPOGRAPHY,
  dark: DARK_TYPOGRAPHY,
  natural: NATURAL_TYPOGRAPHY,
  classic: CLASSIC_TYPOGRAPHY,
  combo: COMBO_TYPOGRAPHY,
};